/**
 * components/rightbar/TabBodyHost.tsx —— 停靠面**正文宿主**：按停靠面树渲染 1–2 格，每格渲染其活跃 tab 的正文座位。
 *
 * 真源：`102:frontend_src/src/components/rightbar/TabBodyHost.vue`（75 行）——抄形态/尺寸/交互，译 Vue→React+TS。
 * 依据：设计件 §6.0-1（面板容器：`push`／`fullscreen` 由框架管；**最多两格、默认均分、
 *       分隔条限 20%–80%**）／§6.1（每会话一个停靠面）／§6.1.1（结算规则：展开态永远至少一个 tab）
 *       ／§2.4（`RightTabStrip` **不渲染正文**；正文只经注册的 `RightTabBody.<type>`）。
 * 边界（逐条承源件）：**不算几何**（比率取自规划器给出的 `ratio`，不是像素）；不建 tab、不发请求、不读会话事件。
 *
 * Vue→React 差异点（登记）：
 *   ① `emit('open-address'…)` 转发 ⇒ 同名回调 props 转发（事件载荷形制逐字不变）；
 *   ② `v-for`＋`:key` ⇒ `map`＋`key`；`:style="{flexGrow: String(pane.ratio || 1)}"` ⇒ `style={{flexGrow: …}}`
 *      （`String(...)` 形制逐字保留）；
 *   ③ 零取数：正文页数据经 `bodyProps` 透传（本件不持业务态）。
 */
import React from "react";
import { TabBodySeat, releaseClosedSeats, type TabBodyRecord } from "./TabBodySeat";

/** 分栏视图（承源件 `useSurface.panes`：`[{paneId, ratio, activeTabId, tabs[]}]`） */
export interface TabPaneRec {
  paneId: string;
  ratio?: number;
  activeTabId?: string | null;
  tabs?: TabBodyRecord[];
}

export interface TabBodyHostProps {
  /** 分栏视图（只读；宿主注入） */
  panes?: TabPaneRec[];
  /** 当前会话 id（页类型正文的数据作用域） */
  sessionId?: string | null;
  /** 宿主注入的页数据/动作（透传至每格正文座位） */
  bodyProps?: Record<string, unknown>;
  /** 打开资源地址（承源件 emit `open-address`） */
  onOpenAddress?: (e: { address: string; kind?: string; fromTabId: string }) => void;
  /** 按 kind 开页 tab（承源件 emit `open-kind`） */
  onOpenKind?: (e: { kind: string; fromTabId: string }) => void;
  /** 关闭 tab（承源件 emit `close-tab`） */
  onCloseTab?: (tabId: string) => void;
  /** 聚焦 tab（承源件 emit `focus-tab`） */
  onFocusTab?: (tabId: string) => void;
  /** ★B12 配套②：刷新命令宿主接线（保活面切回可刷新；缺省回落正文 `bodyProps.onRefresh`） */
  onRefresh?: (e: { tabId: string }) => void;
}

/** 分隔条比例下限（设计件 §6.1.1／§6.2 逐字：「最多两格（默认均分、**分隔条限 20%–80%**）」） */
export const SPLIT_MIN_RATIO = 0.2;
/** 分隔条比例上限（同上，同句逐字） */
export const SPLIT_MAX_RATIO = 0.8;

/**
 * 格宽比例夹取（**20%–80%**）。设计件 §6.1.1 明写「分隔条限 20%–80%」，且「格宽比例**不得**在
 * `plan(state, action)` 里算——`paneSplitRatio` 归 `LayoutState`、由 frame 解算」。
 * 102 真源（`components/rightbar/TabBodyHost.vue`）本身无此 clamp（旧树由 `layout/frame.js` 承担）
 * ⇒ 新树把**格宽比例的唯一实现处**收敛到本件（禁第二处算）；本批只出**纯函数＋断言**，
 * 分隔条拖拽/比例入口的接线归 main（避免改既有渲染破坏 `tests/rightbar.tabbodies` 的既有断言）。
 * 非法/缺省（非有限数）⇒ 取缺省 1（源件 `pane.ratio || 1` 同判法），再夹到值域。
 */
export function clampSplitRatio(ratio: unknown): number {
  const n = typeof ratio === "number" && Number.isFinite(ratio) ? ratio : 1;
  return Math.min(Math.max(n, SPLIT_MIN_RATIO), SPLIT_MAX_RATIO);
}

/** 格的活跃 tab（无 ⇒ null；逐字承源件 activeTabOf） */
function activeTabOf(pane: TabPaneRec): TabBodyRecord | null {
  const tabs = pane.tabs ?? [];
  const hit = tabs.find((t) => t.tabId === pane.activeTabId);
  return hit || tabs[0] || null;
}

/** 停靠面正文宿主 */
export function TabBodyHost(props: TabBodyHostProps): React.ReactElement {
  const panes = props.panes ?? [];
  const sessionId = props.sessionId ?? null;
  /** ★B12 配套③（关闭释放）：本会话里已从停靠面消失的 tab ⇒ 释放其保活座位（句柄执行＋root 销毁） */
  const liveTabIds = panes.flatMap((pane) => (pane.tabs ?? []).map((t) => t.tabId));
  React.useEffect(() => {
    releaseClosedSeats(sessionId, liveTabIds);
  });
  return (
    <div className="xg-tab-body-host" data-testid="rightbar-tab-body">
      {panes.map((pane) => {
        const active = activeTabOf(pane);
        return (
          <section
            key={pane.paneId}
            className="xg-tab-body-host__pane"
            data-pane-id={pane.paneId}
            style={{ flexGrow: String(pane.ratio || 1) }}
          >
            {active ? (
              <TabBodySeat
                key={`${sessionId}:${active.tabId}`}
                tab={active}
                sessionId={sessionId}
                bodyProps={props.bodyProps}
                onOpenAddress={props.onOpenAddress}
                onOpenKind={props.onOpenKind}
                onCloseTab={props.onCloseTab}
                onFocusTab={props.onFocusTab}
                onRefresh={props.onRefresh}
              />
            ) : null}
          </section>
        );
      })}
      <style>{STYLE}</style>
    </div>
  );
}

/** 自包含样式（源件 `<style scoped>` 逐字译入；真名 token；`xg-tab-body-host` 前缀隔离） */
import { tabBodyHostStyle as STYLE } from "../../styles/inlineStyles";

export default TabBodyHost;
